/*
 * <pot-selector> — optional pot add-on for the product form (snippets/pot-selector.liquid).
 *
 * Contract:
 * - id="pot-selector-{sectionId}", data-section="{sectionId}"; pairs with form#product-form-{sectionId}.
 * - Radios .pot-selector__input (value = pot variant id, '' = nursery pot / no pot, data-price in cents).
 * - [data-pot-selector-data] JSON holds every pot that fits ANY size of this plant, so variant
 *   changes rebuild tiles client-side from [data-pot-selector-template] with no fetch.
 * - global.js VariantSelects calls setVariant(variant) on each variant change.
 * - product-form.js reads `selectedPot` on submit and adds pot + plant in one request.
 * - Plant size = last variant option, number before " ("; a pot fits when
 *   size <= pot.size <= size + data-tolerance.
 */
if (!customElements.get('pot-selector')) {
  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(cents, (window.theme && window.theme.moneyFormat) || '£{{amount}}');
    }
    return `£${(cents / 100).toFixed(2)}`;
  };

  const getVariantSize = (variant) => {
    const options = Array.isArray(variant.options) ? variant.options : [];
    if (!options.length) return NaN;
    return parseInt(String(options[options.length - 1]).split(' (')[0], 10);
  };

  const defer = (task) => {
    if (window.scheduler && typeof window.scheduler.postTask === 'function') {
      window.scheduler.postTask(task, { priority: 'user-visible' });
    } else {
      setTimeout(task, 0);
    }
  };

  class PotSelector extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;
      this.section = this.dataset.section;
      this.track = this.querySelector('[data-pot-selector-track]');
      this.sub = this.querySelector('[data-pot-selector-sub]');
      this.template = this.querySelector('[data-pot-selector-template]');
      this.pots = undefined; // parsed lazily from [data-pot-selector-data]
      this.rendered = false;
      this.plantAvailable = true; // server only renders the element visible for an in-stock variant
      this.defaultAddLabel = null;

      this.addEventListener('change', (event) => {
        if (event.target.classList.contains('pot-selector__input')) this.onChange();
      });
      this.addEventListener('focusin', (event) => this.toggleFocus(event.target, true));
      this.addEventListener('focusout', (event) => this.toggleFocus(event.target, false));

      this.syncTileState();
      if (this.selectedPot) this.updateAddButton(); // e.g. radio restored by back/forward cache

      // global.js may have changed variant before this script (deferred) upgraded the element.
      const variantPicker = document.querySelector(
        `variant-radios[data-section="${this.section}"], variant-selects[data-section="${this.section}"]`
      );
      const variant = variantPicker && variantPicker.currentVariant;
      if (variant && String(variant.id) !== this.dataset.currentVariant) this.setVariant(variant);
    }

    get form() {
      return document.getElementById(`product-form-${this.section}`);
    }

    get plantPrice() {
      return Number(this.dataset.plantPrice) || 0;
    }

    get selectedPot() {
      if (this.hidden) return null;
      const input = this.querySelector('.pot-selector__input:checked');
      if (!input || !input.value) return null;
      const name = input.closest('.pot-selector__tile')?.querySelector('.pot-selector__name');
      return {
        id: Number(input.value),
        price: Number(input.dataset.price) || 0,
        name: name ? name.textContent.trim() : '',
        handle: this.dataset.plantHandle,
      };
    }

    getPots() {
      if (this.pots !== undefined) return this.pots;
      const script = this.querySelector('[data-pot-selector-data]');
      try {
        const parsed = script ? JSON.parse(script.textContent) : {};
        this.pots = Array.isArray(parsed.pots) ? parsed.pots : [];
      } catch (error) {
        console.error('Could not parse pot selector data:', error);
        this.pots = [];
      }
      return this.pots;
    }

    setVariant(variant) {
      if (!variant) return;
      const variantId = String(variant.id);
      if (this.rendered && this.dataset.currentVariant === variantId) {
        this.updateAddButton(); // toggleAddButton just reset the label
        return;
      }
      this.dataset.currentVariant = variantId;
      this.dataset.plantPrice = variant.price;
      this.plantAvailable = Boolean(variant.available);

      if (!this.plantAvailable) {
        this.hidden = true;
        const nursery = this.querySelector('.pot-selector__input[value=""]');
        if (nursery) nursery.checked = true;
        this.syncTileState();
        this.updateAddButton();
        return;
      }

      // DOM work runs after the interaction paints so it stays out of INP; a newer variant wins.
      defer(() => {
        if (this.dataset.currentVariant !== variantId) return;
        this.render(variant);
      });
    }

    render(variant) {
      const previous = this.selectedPot;
      this.rendered = true;

      const size = getVariantSize(variant);
      const tolerance = Number(this.dataset.tolerance) || 0;
      const matches = Number.isFinite(size)
        ? this.getPots().filter((pot) => pot.size >= size && pot.size <= size + tolerance)
        : [];

      if (this.track && this.template) {
        const nursery = this.track.querySelector('.pot-selector__tile--nursery');
        const more = this.track.querySelector('[data-pot-selector-more]');
        const fragment = document.createDocumentFragment();
        if (nursery) fragment.appendChild(nursery);
        matches.forEach((pot) => fragment.appendChild(this.buildTile(pot)));
        if (more) fragment.appendChild(more);
        this.track.replaceChildren(fragment);
        this.track.scrollLeft = 0;
      }

      const keep = previous && matches.some((pot) => Number(pot.id) === previous.id);
      const inputs = this.querySelectorAll('.pot-selector__input');
      inputs.forEach((input) => {
        input.checked = keep ? Number(input.value) === previous.id : input.value === '';
      });

      if (this.sub && this.dataset.fitText && Number.isFinite(size)) {
        this.sub.textContent = this.dataset.fitText.replace('{size}', `${size} cm`);
      }

      this.hidden = matches.length === 0;
      this.syncTileState();
      this.updateAddButton();
    }

    buildTile(pot) {
      const tile = this.template.content.firstElementChild.cloneNode(true);
      const input = tile.querySelector('.pot-selector__input');
      if (input) {
        input.value = pot.id;
        input.checked = false;
        input.dataset.price = pot.price;
        input.dataset.size = pot.size;
      }
      const img = tile.querySelector('.pot-selector__img');
      if (img && pot.img) {
        img.src = pot.img;
        img.srcset = pot.img2x ? `${pot.img} 1x, ${pot.img2x} 2x` : '';
        img.alt = pot.alt || '';
      }
      const name = tile.querySelector('.pot-selector__name');
      if (name) name.textContent = pot.name || '';
      const price = tile.querySelector('.pot-selector__price');
      if (price) price.textContent = `+${pot.priceFormatted || formatMoney(pot.price)}`;
      return tile;
    }

    onChange() {
      this.syncTileState();
      this.updateAddButton();
      this.dispatchEvent(new CustomEvent('pot-selector:change', {
        bubbles: true,
        detail: { pot: this.selectedPot },
      }));
    }

    toggleFocus(target, focused) {
      if (!target.classList || !target.classList.contains('pot-selector__input')) return;
      const tile = target.closest('.pot-selector__tile');
      if (!tile) return;
      let visible = focused;
      try { visible = focused && target.matches(':focus-visible'); } catch (e) { /* no :focus-visible */ }
      tile.classList.toggle('is-focused', visible);
    }

    syncTileState() {
      this.querySelectorAll('.pot-selector__tile').forEach((tile) => {
        const input = tile.querySelector('.pot-selector__input');
        tile.classList.toggle('is-selected', Boolean(input && input.checked));
      });
    }

    updateAddButton() {
      const form = this.form;
      if (!form) return;
      const button = form.querySelector('[name="add"]') || form.querySelector('[type="submit"]');
      const label = button && button.querySelector(':scope > span');
      const pot = this.selectedPot;

      if (pot) form.dataset.potSelected = String(pot.id);
      else delete form.dataset.potSelected;

      // Sold out / unavailable / mid-submit: global.js and product-form.js own the label.
      if (!label || !this.plantAvailable || button.disabled || button.getAttribute('aria-disabled') === 'true') return;

      if (this.defaultAddLabel === null) {
        this.defaultAddLabel = (window.variantStrings && window.variantStrings.addToCart) || label.textContent.trim();
      }
      label.textContent = pot
        ? `${this.dataset.addLabel} · ${formatMoney(this.plantPrice + pot.price)}`
        : this.defaultAddLabel;
    }
  }

  customElements.define('pot-selector', PotSelector);
}
